/* global Blockly */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { registerAssistingBlocks } from './blockVersionConfig';
import DatesCategory from "./blockVersions/v1/blocks/Dates";
import WidgetValues from "./blockVersions/v1/blocks/WidgetValues"
import AppActions from "./blockVersions/v1/blocks/AppActions";
import EnvironmentVariables from './blockVersions/v1/blocks/Variables';
import XSightTopics from './blockVersions/v1/blocks/XSightTopics';
import Address from './blockVersions/v3/blocks/Address';
import Logic from "./blockVersions/v1/blocks/Logic";
import Loops from "./blockVersions/v1/blocks/Loops";
import MathCategory from "./blockVersions/v1/blocks/Math";
import Text from "./blockVersions/v1/blocks/Text";
import Lists from "./blockVersions/v1/blocks/Lists";
import Colour from "./blockVersions/v1/blocks/Colour";
import Debug from './blockVersions/v1/blocks/Debug';
import Legacy from "./Legacy";
import Search from "./Search";
import SearchBar from './Search/SearchBar'
import ToolboxList from './ToolboxList';
import MyBlocks from "./MyBlocks";
import JSBlocks from './JSBlocks';
import Scrollbar from "smooth-scrollbar";
import Connections from "./blockVersions/v1/blocks/Connections";
import MobiControl from './blockVersions/v1/blocks/MobiControl';
import Repeater from "./blockVersions/v1/blocks/Repeater";
import TableView from "./blockVersions/v1/blocks/Table";
import Sound from './blockVersions/v1/blocks/Sound';

export const TOOLBOX_COLLAPSED_WIDTH = 45;
export const TOOLBOX_EXPANDED_WIDTH = 199;

// how long/often to keep retrying, in componentDidMount, while waiting for
// Blockly.mainWorkspace to become available (see _initializeToolboxBehaviors)
const MOUNT_WAIT_RETRY_MS = 20;
const MOUNT_WAIT_MAX_ATTEMPTS = 250;

let onCanvasClick = null;
let onWorkspaceBlockDrag = null;
let onWorkspaceBlockDragEnd = null;

const SnapBlocks = ({toolboxList, context, setRestBlockError, openJSEditor}) => {
    /*Connections blocks to be added when integrated with API server*/
    /*<Connections context={this.props.context} blocks={toolboxList["Connections"]}/>*/
    const snapBlocks = [
        <Legacy context={context} key={"legacy"}/>,
        <WidgetValues context={context} blocks={toolboxList["Widget Values"]} key={"widgetValues"}/>,
        <AppActions context={context} blocks={toolboxList["App Actions"]} key={"appActions"}/>,
        <Connections context={context} blocks={toolboxList["Connections"]} key={"connections"} setRestBlockError = {setRestBlockError}/>,
        <JSBlocks context={context} key={"JSVariables"} openJSEditor={openJSEditor}/>,
        <EnvironmentVariables context={context} blocks={toolboxList["Global Variables"]} key={"envVars"}/>,
        <XSightTopics context={context} blocks={toolboxList["XSight Topics"]} key={"xSightTopics"}/>,
        <Address context={context} blocks={toolboxList["Address"]} key={"address"}/>,
        // eslint-disable-next-line react/no-unknown-property -- colour/custom/iconclass are Blockly toolbox XML attributes, not DOM props
        <category key="variables" id="variables" name="Local Variables" colour="%{BKY_VARIABLES_HUE}" custom="VARIABLE" iconclass="category with-icon-paths snap-icon-agex_dual"></category>,
    ];

    const isRendered = snapBlocks.reduce((rendered, component) => {
        return !!(rendered || component.type( component.props));
    }, false);

    return (
        <React.Fragment>
            {snapBlocks}
            {
                isRendered
                    ? <sep id="separator_defaults"></sep>
                    : null
            }
        </React.Fragment>
    )
}

SnapBlocks.propTypes = {
    toolboxList: PropTypes.object.isRequired,
    context: PropTypes.object.isRequired,
    setRestBlockError: PropTypes.func,
    openJSEditor: PropTypes.func
};

export default class Toolbox extends Component {
    constructor(props) {
        super(props);
        this.state = {
            flyoutOpened: false,
            toolboxList: this._getToolboxList(props),
            collapsed: true,
            pinned: false,
            isSearchExpanded: false
        };
        // Register assisting blocks via versioning system (chains from latest to V1)
        registerAssistingBlocks({ context: props.context });
    }

    _getToolboxList(props) {
        return ToolboxList.build(props);
    }

    componentDidUpdate(prevProps) {
        if (prevProps === this.props) {
            // props reference is unchanged, so this update was triggered by local
            // state only (e.g. collapse/pin) - nothing to re-sync from props
            return;
        }
        // Update toolbox in next tick to make sure new myBlocks are injected in the DOM
        const isSearchChanged = prevProps.searchQuery !== this.props.searchQuery;
        setTimeout(() => this._updateToolbox(this.props, isSearchChanged));
    }

    componentDidMount() {
        this._initializeToolboxBehaviors();
    }

    // Blockly.mainWorkspace is not guaranteed to exist yet when Toolbox mounts:
    // WorkspaceContainer defers Blockly.inject() to its own setTimeout(0) (it has
    // to wait for async init info from the host), and this component schedules its
    // own setTimeout(0) independently in componentDidMount. Which one the browser
    // runs first depends on React's commit order (children mount before parents'
    // effects) and isn't a safe assumption to rely on across refactors of either
    // component's lifecycle methods - so poll briefly instead of assuming a fixed
    // ordering between the two unrelated timeouts. Without this, the whole setup
    // below silently throws (Blockly.mainWorkspace is undefined) and the block
    // panel's expand/collapse and keep-flyout-open behaviors never get wired up.
    _initializeToolboxBehaviors(attempt = 0) {
        if (!Blockly.mainWorkspace) {
            if (attempt >= MOUNT_WAIT_MAX_ATTEMPTS) {
                console.warn('Toolbox: Blockly.mainWorkspace never became available; block panel behaviors (auto-expand/collapse, keep-flyout-open) were not initialized');
                return;
            }
            this._mountRetryTimeoutId = setTimeout(() => this._initializeToolboxBehaviors(attempt + 1), MOUNT_WAIT_RETRY_MS);
            return;
        }
        Blockly.mainWorkspace.addChangeListener(blocklyEvent => {
            if (!this.state.pinned && blocklyEvent.type == Blockly.Events.UI && blocklyEvent.element == 'category') {
                if (!blocklyEvent.oldValue) {
                    this.expandToolbox();
                    this.setState({ collapsed: false });
                } else if (!blocklyEvent.newValue) {
                    this.collapseToolbox();
                    this.setState({ collapsed: true });
                }
            }
        });
        this._keepFlyoutOpenOnBlockCreate();
        this._wrapWorkspaceClickForCollapse();
        this._wrapBlockDragForFlyoutCollapse();
        onCanvasClick = this._handleCanvasClick;
        onWorkspaceBlockDrag = this._handleWorkspaceBlockDrag;
        onWorkspaceBlockDragEnd = this._finishWorkspaceBlockDrag;
    }

    componentWillUnmount() {
        clearTimeout(this._mountRetryTimeoutId);
        if (onCanvasClick === this._handleCanvasClick) {
            onCanvasClick = null;
        }
        if (onWorkspaceBlockDrag === this._handleWorkspaceBlockDrag) {
            onWorkspaceBlockDrag = null;
        }
        if (onWorkspaceBlockDragEnd === this._finishWorkspaceBlockDrag) {
            onWorkspaceBlockDragEnd = null;
        }
        this._pendingFlyoutCollapseGesture = null;
    }

    // Toolbar actions and workspace-block drags close the visible flyout.
    // A pinned sidebar stays expanded; an unpinned one follows the usual
    // full-collapse path, including clearing its selected category.
    closeFlyoutForExternalAction = (dragGesture = null) => {
        const workspace = Blockly.mainWorkspace;
        const flyout = workspace?.getFlyout?.();
        if (!flyout?.isVisible?.()) return;

        if (this.state.pinned) {
            flyout.hide();
        } else if (dragGesture) {
            // Shrinking the sidebar while Blockly has a block on its drag
            // surface temporarily distorts connected blocks. Hide the flyout
            // now, but wait until the gesture is finished to change width.
            flyout.hide();
            this._pendingFlyoutCollapseGesture = dragGesture;
        } else {
            this.collapseButtonHandler();
        }

        // Blockly's recordDeleteAreas uses the flyout rectangle even when it
        // is hidden. Once the flyout closes mid-drag, only the actual sidebar
        // should remain a drop-to-delete target.
        workspace.recordDeleteAreas?.();
        const toolboxRect = workspace.getToolbox?.()?.getClientRect?.();
        if (toolboxRect) workspace.deleteAreaToolbox_ = toolboxRect;
    }

    _finishWorkspaceBlockDrag = gesture => {
        if (this._pendingFlyoutCollapseGesture !== gesture) return;
        this._pendingFlyoutCollapseGesture = null;
        this.collapseButtonHandler();
    }

    _handleWorkspaceBlockDrag = (gesture, moveEvent) => {
        const workspace = Blockly.mainWorkspace;
        const block = gesture.blockDragger_?.draggingBlock_;
        const flyout = workspace?.getFlyout?.();
        if (gesture.flyout_ || !block || block.workspace !== workspace || block.isInFlyout ||
            !flyout?.isVisible?.()) return;

        // Use the visible flyout bounds, not Blockly's infinite-height
        // drag-to-delete area or the bounding area of a connected stack.
        // Blockly uses clientX/clientY from this same event for deletion.
        const rect = flyout.svgGroup_?.getBoundingClientRect();
        if (rect && rect.right > rect.left && rect.bottom > rect.top &&
            Number.isFinite(moveEvent?.clientX) && Number.isFinite(moveEvent?.clientY) &&
            moveEvent.clientX >= rect.left && moveEvent.clientX < rect.right &&
            moveEvent.clientY >= rect.top && moveEvent.clientY < rect.bottom) {
            this.closeFlyoutForExternalAction(gesture);
        }
    }

    // Observe the completed move (after Blockly has positioned the block).
    // Unlike the drag-surface translate hook, this also works when Blockly
    // moves a block without a separate SVG drag surface.
    _wrapBlockDragForFlyoutCollapse() {
        const gestureProto = Blockly.Gesture?.prototype;
        if (!gestureProto?.handleMove || gestureProto.handleMove.__flyoutCollapseWrapped) return;
        const originalHandleMove = gestureProto.handleMove;
        const wrappedHandleMove = function(...args) {
            const result = originalHandleMove.apply(this, args);
            onWorkspaceBlockDrag?.(this, args[0]);
            return result;
        };
        wrappedHandleMove.__flyoutCollapseWrapped = true;
        gestureProto.handleMove = wrappedHandleMove;

        // Both normal pointer-up and cancelled gestures need to finish the
        // pending sidebar collapse, after Blockly returns the block to its
        // workspace and has updated its connected stack.
        const wrapDragEnd = method => {
            const original = gestureProto[method];
            if (!original || original.__flyoutCollapseWrapped) return;
            const wrapped = function(...args) {
                const result = original.apply(this, args);
                onWorkspaceBlockDragEnd?.(this);
                return result;
            };
            wrapped.__flyoutCollapseWrapped = true;
            gestureProto[method] = wrapped;
        };
        wrapDragEnd('handleUp');
        wrapDragEnd('cancel');
    }

    // collapse on empty-canvas click: full collapse when unpinned, flyout-only
    // when pinned (pinned panel itself stays open)
    _handleCanvasClick = () => {
        const tree = Blockly.mainWorkspace?.getToolbox()?.tree_;
        // only act if a category is actually open
        if (!tree?.getSelectedItem?.()) {
            return;
        }
        if (this.state.pinned) {
            Blockly.mainWorkspace.getFlyout()?.hide();
        } else {
            this.collapseButtonHandler();
        }
    }

    // Keep the flyout open when a block is clicked or dragged out of it.
    // autoClose is a per-flyout Blockly setting (default true); setting it on
    // the toolbox flyout instance stops createBlock from hiding the flyout.
    _keepFlyoutOpenOnBlockCreate() {
        const flyout = Blockly.mainWorkspace?.getFlyout?.();
        if (flyout) {
            flyout.autoClose = false;
        } else {
            console.warn('Toolbox: flyout not found, block panel will auto-close when blocks are used');
        }
    }

    // Collapse the flyout on an empty-canvas click. Blockly only calls
    // doWorkspaceClick_ for real clicks (not drags or drops), so those
    // never collapse the flyout.
    _wrapWorkspaceClickForCollapse() {
        const gestureProto = Blockly.Gesture?.prototype;
        if (!gestureProto?.doWorkspaceClick_ || gestureProto.doWorkspaceClick_.__collapseToolboxWrapped) {
            if (!gestureProto?.doWorkspaceClick_) {
                console.warn('Toolbox: Blockly.Gesture.prototype.doWorkspaceClick_ missing, canvas click will not collapse the block panel');
            }
            return;
        }
        const originalDoWorkspaceClick = gestureProto.doWorkspaceClick_;
        const wrappedDoWorkspaceClick = function() {
            originalDoWorkspaceClick.call(this);
            // A click on empty space *inside* the flyout (between/around the
            // blocks it contains) is also a "workspace click" as far as
            // Blockly's gesture handling is concerned - the flyout has its
            // own internal WorkspaceSvg, and Flyout.prototype.onMouseDown_
            // starts the gesture via handleFlyoutStart, which sets
            // gesture.flyout_ before ever reaching doWorkspaceClick_. So
            // gesture.flyout_ being set here means the click landed in the
            // flyout, not on the main workspace canvas, and should not
            // collapse/hide it.
            if (this.flyout_) {
                return;
            }
            onCanvasClick?.();
        };
        wrappedDoWorkspaceClick.__collapseToolboxWrapped = true;
        wrappedDoWorkspaceClick.__originalDoWorkspaceClick = originalDoWorkspaceClick;
        gestureProto.doWorkspaceClick_ = wrappedDoWorkspaceClick;
    }

    addExpandedClass() {
        const toolboxRows = document.querySelectorAll('.blocklyTreeRow');
        [...toolboxRows].forEach(row => {
            row.classList.add("blocklyTreeRowExpanded");
        });
    }

    collapseToolbox = () => {
        this.setState({ collapsed: true });
        const toolboxDiv = document.querySelector('.blocklyToolboxDiv');
        const toolboxRows = document.querySelectorAll('.blocklyTreeRow');
        const selectedRows = document.querySelectorAll('.blocklyTreeSelected');
        const flyout = Blockly.mainWorkspace.getFlyout();
        toolboxDiv.style.width = `${TOOLBOX_COLLAPSED_WIDTH}px`;
        toolboxDiv.style.height = "100%";
        flyout?.hide();
        [...toolboxRows].forEach(row => {
            row.classList.remove("blocklyTreeRowExpanded");
        });
        [...selectedRows].forEach(row => {
            row.classList.remove("blocklyTreeSelected");
            row.style.backgroundColor = "";
        });

        // make toolbox scrollbars transparent
        const scrollbars = document.querySelectorAll('.scrollbar-thumb');
        [...scrollbars].forEach(scrollbar => {
            scrollbar.style.backgroundColor = 'transparent';
        });

        // collapse My Libraries category
        const toolbox = Blockly.mainWorkspace.getToolbox();
        toolbox?.tree_?.collapseChildren();

        Blockly.mainWorkspace.recordDeleteAreas();

        let toolboxTrashcan = document.querySelector(".toolbox-trashcan");
        toolboxTrashcan.classList.remove("expanded");
        globalThis.dispatchEvent(new Event('resize'));
    }

    expandToolbox = () => {
        this.setState({ collapsed: false });
        const toolboxDiv = document.querySelector('.blocklyToolboxDiv');
        const toolboxRows = document.querySelectorAll('.blocklyTreeRow');
        toolboxDiv.style.width = `${TOOLBOX_EXPANDED_WIDTH}px`;
        toolboxDiv.style.height = "calc(100% - 48px)";
        toolboxDiv.style.marginTop = "48px";
        [...toolboxRows].forEach(row => {
            row.classList.add("blocklyTreeRowExpanded");
        });

        // make toolbox scrollbars visible
        const scrollbars = document.querySelectorAll('.scrollbar-thumb');
        [...scrollbars].forEach(scrollbar => {
            scrollbar.style.backgroundColor = 'rgb(205, 206, 205)';
        });

        Blockly.mainWorkspace.recordDeleteAreas();

        let toolboxTrashcan = document.querySelector(".toolbox-trashcan");
        toolboxTrashcan.classList.add("expanded");
    }

    _initializeScroller () {
        const toolboxDiv = document.querySelector('.blocklyToolboxDiv');
        this.scroller = Scrollbar.init(toolboxDiv, {
            plugins: {
                disableScroll: {'direction': 'x'}
            }
        });
    }

    collapseButtonHandler = () => {
        this.collapseToolbox();
        Blockly.mainWorkspace.getToolbox().refreshSelection();
        Blockly.mainWorkspace.getToolbox().tree_.setSelectedItem(null);
        this.setState({ collapsed: true, pinned: false });
    }

    expandButtonHandler = () => {
        this.expandToolbox();
        this.setState({ collapsed: false, pinned: true });
    }

    onSearchFocus = () => {
        if (!this.state.pinned) {
            this.setState({ collapsed: false, pinned: true, isSearchExpanded: true });
        }
    }

    onSearchBlur = () => {
        if (this.state.isSearchExpanded) {
            this.setState({ pinned: false, isSearchExpanded: false });
        }
    }

    _updateScrollerGeometry () {
        this.scroller?.update();
    }

    // returns the Blockly toolbox tree node for the "My Libraries", or null
    _getMyLibrariesNode () {
        const toolbox = Blockly.mainWorkspace?.getToolbox();
        const children = toolbox?.tree_?.children_;
        if (!children) return null;
        return children.find(child => child.getText?.() === 'My Libraries') || null;
    }

	// smooth scroll sidebar to top, clamp at top of row
    _scrollRowToTop (row) {
        if (!this.scroller || !row) return;

        this.scroller.update();
        this.scroller.scrollIntoView(row, { alignToTop: true, offsetTop: 0 });
    }

    // scroll My Libraries to the top whenever it is expanded
    _attachMyLibrariesScrollHandler () {
        const node = this._getMyLibrariesNode();
        const row = node?.getRowElement();

		// only attach scroll handlers once
        if (!row || row.dataset.scrollHandlerAttached) return;
        row.dataset.scrollHandlerAttached = 'true';

        row.addEventListener('click', () => {
            // wait for row to be toggled first, then scroll
            setTimeout(() => {
                if (node.getExpanded()) {
                    this._scrollRowToTop(row);
                }
            }, 0);
        });
    }

    _attachHandlers () {
        // adjust scrollbar according to number of custom block libraries
        Blockly.mainWorkspace.toolbox_.HtmlDiv.addEventListener("pointerup", () => {
            setTimeout(() => {
                this.scroller?.update();
            }, 0);
        });
        this._isHandlerAttached = true;
    }

    _initToolboxTooltips() {
        document.getElementById('workspaceMountDiv').querySelectorAll('.blocklyTreeRow').forEach(x => {
            let labelContainer = x.querySelector('.blocklyTreeLabel');
            if (!labelContainer?.innerText) return;
            let label = labelContainer.innerText;
            let workspaceDiv = document.getElementById('workspaceMountDiv');
            let tooltipText = Blockly.Tooltips.Toolbox[label];
            let didOpen = false;
            let timeout;
            let appendTooltip = () => {
                let tooltip = document.createElement('div');
                tooltip.innerText = tooltipText;
                tooltip.classList.add("toolboxTooltip");
                let pos = x.getBoundingClientRect();
                tooltip.style.left = (pos.right + 10) + 'px';
                tooltip.style.top = (pos.bottom - 38) + 'px';
                workspaceDiv.appendChild(tooltip);
                setTimeout(() => {
                    tooltip.classList.add("open")
                }, 1);
                didOpen = true
            };
            let removeTooltip = () => {
                clearTimeout(timeout);
                let tt = workspaceDiv.querySelector('.toolboxTooltip');
                if (tt) {
                    tt.remove()
                }
            };

            x.addEventListener('mousemove', () => {
                removeTooltip();
                if (!didOpen) {
                    timeout = setTimeout(appendTooltip, 500);
                }
            });
            x.addEventListener('mouseleave', () => {
                removeTooltip();
                didOpen = false;
            });
            x.addEventListener('click', () => {
                removeTooltip();
                didOpen = false;
            });
        });
    }

    _openSearch () {
        const mainWorkspace = Blockly.Workspace.getAll()[0];
        if (!mainWorkspace?.getToolbox()) { return; }
        const selectedToolboxCategory = mainWorkspace.getToolbox().tree_.children_.find(child => child.element_.innerText === "Search");

        if (selectedToolboxCategory) {
            Blockly.hideChaff(true);
            selectedToolboxCategory.element_.click();
        }
    }

    // hide some categories programmatically
    _updateHiddenCategories() {
        // this is an officially recommended way to hide a category in old Blockly,
        // however:
        // 1. this is not a good solution and more a "workaround"
        // 2. it executes with some delay, so you will see category flashing
        // 3. if you will use search, then the blocks from this category still come up, and the category becomes visible
        //
        // no categories are currently hidden this way; revisit once there's a
        // better solution for hiding a category, or once Blockly is updated
    }

    _updateToolbox (nextProps, isSearchChanged) {
        const { workspace, toolboxRef } = nextProps;
        this.setState(prevState => ({
            ...prevState,
            toolboxList: this._getToolboxList(nextProps)
        }));
        workspace?.updateToolbox(toolboxRef.current);
        Blockly.utils.ensureIconPathsExist();
        this.addExpandedClass();
        !this._isHandlerAttached && this._attachHandlers();
        this._initToolboxTooltips();
        this._attachMyLibrariesScrollHandler();
        this._updateHiddenCategories();
        isSearchChanged && this._openSearch(nextProps.searchQuery);
        this._initializeScroller();
        this._updateScrollerGeometry();
        this.state.collapsed && this.collapseToolbox();
    }

    render() {
        if (!this.props.context) {
            return null;
        }

        const { onSearch, searchQuery, setRestBlockError, playServerSound } = this.props;
        const { toolboxList } = this.state;
        const toolboxLibraries = toolboxList['My Libraries'] || {};
        const filteredLibraries = this.props.libraries.map(library => ({
            ...library,
            blocks: Object.values(toolboxLibraries[library.name]?.childComponents || {}).map(child => child.component),
            hasExcludedBlocks: toolboxLibraries[library.name]?.hasExcludedBlocks || false
        }));

        return (
            <div>
                <SearchBar onSearch={onSearch} searchQuery={searchQuery} collapsed={this.state.collapsed}
                    collapseButtonHandler={this.collapseButtonHandler} expandButtonHandler={this.expandButtonHandler}
                    onSearchFocus={this.onSearchFocus} onSearchBlur={this.onSearchBlur} />
                <svg style={{ display: 'none' }}>
                    <xml id="toolbox" ref={this.props.toolboxRef}>
                        {
                            searchQuery
                                ? <Search searchQuery={searchQuery} context={this.props.context} blocks={toolboxList}/>
                                : null
                        }
                        <SnapBlocks context={this.props.context} toolboxList={toolboxList} setRestBlockError = {setRestBlockError} openJSEditor={this.props.openJSEditor}/>

                        <Logic context={this.props.context} blocks={toolboxList["Logic"]}/>
                        <Loops context={this.props.context} blocks={toolboxList["Loops"]}/>
                        <MathCategory context={this.props.context} blocks={toolboxList["Math"]}/>
                        <Text context={this.props.context} blocks={toolboxList["Text"]}/>
                        <Lists context={this.props.context} blocks={toolboxList["Lists"]}/>
                        <Colour context={this.props.context} blocks={toolboxList["Colour"]}/>
                        <DatesCategory context={this.props.context} blocks={toolboxList["Dates"]}/>
                        <Repeater context={this.props.context} blocks={toolboxList["Repeater"]}/>
                        <TableView context={this.props.context} blocks={toolboxList["Table"]} setRestBlockError = {setRestBlockError}/>
                        <Sound context={this.props.context} blocks={toolboxList["Sound"]} playServerSound={playServerSound} />
                        <MobiControl context={this.props.context} blocks={toolboxList["MobiControl"]} openJSEditor={this.props.openJSEditor}/>
                        <Debug context={this.props.context} blocks={toolboxList["Debug Tools"]}/>

                        <sep id="separator_defaults"></sep>

                        <MyBlocks context={this.props.context} libraries={filteredLibraries}/>
                    </xml>
                </svg>
                <div className={"toolbox-trashcan snap-icon-delete"}>
                </div>
            </div>
        );
    }
}

Toolbox.propTypes = {
    context: PropTypes.object,
    searchQuery: PropTypes.string,
    onSearch: PropTypes.func,
    setRestBlockError: PropTypes.func,
    playServerSound: PropTypes.func,
    libraries: PropTypes.array,
    toolboxRef: PropTypes.object,
    openJSEditor: PropTypes.func,
    workspace: PropTypes.object
};
